// lib/alova.ts
import { createAlova } from "alova";
import SolidHook from "alova/solid";
import customFetchAdapter from "./adapter";
import {
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  clearAllTokens,
} from "./auth";

const baseURL = import.meta.env.VITE_URL_SSO;

// --- refresh token queue ---
let isRefreshing = false;
let pendingQueue: (() => void)[] = [];

// --- function refresh token ---
async function refreshAccessToken() {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) throw new Error("No refresh token");

  const res = await fetch(`${baseURL}/auth/refresh-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshTokenValue }),
  });

  if (!res.ok) throw new Error("Refresh token failed");

  const data = await res.json();
  setToken(data.data.access_token);
  setRefreshToken(data.data.refresh_token);
  return data.data.access_token;
}

// --- type helper untuk _retry flag ---
interface RetryableConfig {
  _retry?: boolean;
  headers: Record<string, string>;
  [key: string]: any;
}

// --- authAlova (misal login, register) ---
export const authAlova = createAlova({
  baseURL,
  requestAdapter: customFetchAdapter,
  statesHook: SolidHook,
  responded: { onSuccess: (res) => res },
});

// --- apiAlova (protected API + auto refresh token) ---
export let apiAlova = createAlova({
  baseURL,
  requestAdapter: customFetchAdapter,
  statesHook: SolidHook,
  beforeRequest: (method) => {
    const token = getToken();
    if (token) {
      method.config.headers = {
        ...method.config.headers,
        Authorization: `Bearer ${token}`,
      };
    }
  },
  responded: {
    onSuccess: (res, meta) => {
      return {
        ...(res as any),
        fromCache: meta.fromCache,
      };
    },
    async onError(err, method) {
      const config = method.config as RetryableConfig;

      if (err.status === 401 && !config._retry) {
        config._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            pendingQueue.push(async () => {
              try {
                const retryRes = await method.send();
                resolve(retryRes);
              } catch (e) {
                reject(e);
              }
            });
          });
        }

        try {
          isRefreshing = true;
          const newToken = await refreshAccessToken();

          pendingQueue.forEach((cb) => cb());
          pendingQueue = [];

          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${newToken}`,
          };
          return await method.send();
        } catch (e) {
          clearAllTokens();
          window.location.href = "/";
          throw e;
        } finally {
          isRefreshing = false;
        }
      }

      throw err;
    },
  },
});

// --- helper function reset apiAlova ---
export function resetApiAlova() {
  apiAlova = createAlova({
    baseURL,
    requestAdapter: customFetchAdapter,
    statesHook: SolidHook,
    beforeRequest: (method) => {
      const token = getToken();
      if (token) {
        method.config.headers = {
          ...(method.config.headers || {}), // Fallback to empty object if undefined
          Authorization: `Bearer ${token}`,
        };
      }
    },
    responded: {
      onSuccess: (res, meta) => {
        return {
          ...(res as any),
          fromCache: meta.fromCache,
        };
      },
      async onError(err, method) {
        const config = method.config as RetryableConfig;
        if (err.status === 401 && !config._retry) {
          config._retry = true;

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              pendingQueue.push(async () => {
                try {
                  const retryRes = await method.send();
                  resolve(retryRes);
                } catch (e) {
                  reject(e);
                }
              });
            });
          }

          try {
            isRefreshing = true;
            const newToken = await refreshAccessToken();

            pendingQueue.forEach((cb) => cb());
            pendingQueue = [];

            config.headers = {
              ...config.headers,
              Authorization: `Bearer ${newToken}`,
            };
            return await method.send();
          } catch (e) {
            clearAllTokens();
            window.location.href = "/";
            throw e;
          } finally {
            isRefreshing = false;
          }
        }

        throw err;
      },
    },
  });
}
